import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare, BarChart3, Calendar, Paperclip,
  X, Plus, Send, Users, Globe, Upload, MapPin, Clock,
  Building2, UserCircle, Tag, Sparkles,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import FeedEditor from './FeedEditor';
import type { FeedPost } from '@/types/feed';
import type { FeedTopicListItem, FeedTopicSuggestion, DirectoryUserLookup, WorkgroupLookup, DepartmentLookup } from '@easyflow/shared';

interface PostComposerProps {
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  isAdmin: boolean;
  className?: string;
  fixedTargetType?: string;
  fixedTargetId?: string;
}

interface RecipientEntity {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string;
  _type?: 'user' | 'group' | 'department';
  _label?: string;
}

const POST_TYPES = [
  { value: 'message', label: 'Messaggio', icon: MessageSquare, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700' },
  { value: 'event', label: 'Evento', icon: Calendar, color: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-900/30 border-cyan-200 dark:border-cyan-700' },
  { value: 'poll', label: 'Sondaggio', icon: BarChart3, color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700' },
  // Task #1662 — Feed v2: Q&A e Milestone post types
  { value: 'question', label: 'Domanda', icon: MessageSquare, color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700' },
  { value: 'milestone', label: 'Milestone', icon: BarChart3, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700' },
];

const AVATAR_COLORS = [
  'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-purple-500 to-violet-600',
  'from-orange-500 to-red-600', 'from-pink-500 to-rose-600', 'from-cyan-500 to-blue-600',
];

function getAvatarGradient(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const TYPE_ICON: Record<string, any> = {
  user: UserCircle,
  group: Users,
  department: Building2,
};

const TYPE_COLORS: Record<string, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  group: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  department: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function PostComposer({ onSubmit, isAdmin, className, fixedTargetType, fixedTargetId }: PostComposerProps) {
  const { user } = useAuth();
  const [postType, setPostType] = useState('message');
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [contentJson, setContentJson] = useState<Record<string, unknown> | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollMultiple, setPollMultiple] = useState(false);
  const [pollAnonymous, setPollAnonymous] = useState(false);

  const [eventTitle, setEventTitle] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [availableTopics, setAvailableTopics] = useState<Pick<FeedTopicListItem, 'id' | 'name' | 'color'>[]>([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);

  // Task #1679 — suggerimenti automatici topic
  const [suggestedTopics, setSuggestedTopics] = useState<FeedTopicSuggestion[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const lastSuggestedIdsRef = useRef<string[]>([]);
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const suggestAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    apiClient.get<FeedTopicListItem[]>('/messenger/feed/topics')
      .then(d => { if (d.success) setAvailableTopics(d.data || []); })
      .catch(() => {});
  }, []);

  const toggleTopic = (id: string) => {
    setSelectedTopicIds(prev => {
      if (prev.includes(id)) return prev.filter(t => t !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  // Debounced fetch dei suggerimenti topic in base a titolo + contenuto.
  useEffect(() => {
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    if (availableTopics.length === 0) { setSuggestedTopics([]); return; }
    const plain = contentHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const composite = `${title || ''} ${plain}`.trim();
    if (composite.length < 20) { setSuggestedTopics([]); lastSuggestedIdsRef.current = []; return; }
    suggestTimerRef.current = setTimeout(async () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      setIsSuggesting(true);
      try {
        const data = await apiClient.post<FeedTopicSuggestion[]>(
          '/messenger/feed/topics/suggest',
          { title, content_html: contentHtml },
          { signal: ctrl.signal },
        );
        if (data.success && Array.isArray(data.data)) {
          const list = data.data;
          setSuggestedTopics(list);
          lastSuggestedIdsRef.current = list.map(s => s.id);
        }
      } catch { /* aborted o errore: silent */ }
      finally { setIsSuggesting(false); }
    }, 600);
    return () => { if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current); };
  }, [title, contentHtml, availableTopics.length]);

  const acceptSuggestion = (id: string) => {
    if (selectedTopicIds.includes(id) || selectedTopicIds.length >= 3) return;
    setSelectedTopicIds(prev => [...prev, id]);
  };

  const dismissSuggestion = (id: string) => {
    setSuggestedTopics(prev => prev.filter(s => s.id !== id));
  };

  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState<RecipientEntity[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientEntity[]>([]);
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const recipientTimerRef = useRef<NodeJS.Timeout>();
  const recipientInputRef = useRef<HTMLInputElement>(null);

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Utente';
  const userInitial = (user?.firstName || 'U')[0];

  const targetType = selectedRecipients.length > 0 ? 'direct' : 'global';

  const hasGroupOrDept = selectedRecipients.some(r => r._type === 'group' || r._type === 'department');

  useEffect(() => {
    if (recipientTimerRef.current) clearTimeout(recipientTimerRef.current);
    if (!recipientSearch.trim() || recipientSearch.length < 2) {
      setRecipientResults([]);
      setShowRecipientDropdown(false);
      return;
    }
    recipientTimerRef.current = setTimeout(async () => {
      try {
        const allResults: RecipientEntity[] = [];
        const q = encodeURIComponent(recipientSearch);
        const [usersRes, groupsRes, deptsRes] = await Promise.allSettled([
          apiClient.get<DirectoryUserLookup[]>(`/users/search?search=${q}&limit=5`),
          apiClient.get<WorkgroupLookup[]>(`/workgroups?search=${q}&limit=5`),
          apiClient.get<DepartmentLookup[]>(`/departments?search=${q}&limit=5`),
        ]);
        if (usersRes.status === 'fulfilled' && usersRes.value.success) {
          allResults.push(...(usersRes.value.data || []).map((u) => ({
            id: u.id, first_name: u.first_name, last_name: u.last_name, email: u.email, _type: 'user' as const, _label: 'Utente',
          })));
        }
        if (groupsRes.status === 'fulfilled' && groupsRes.value.success) {
          allResults.push(...(groupsRes.value.data || []).map((g) => ({
            id: g.id, first_name: g.name, last_name: '', _type: 'group' as const, _label: 'Gruppo',
          })));
        }
        if (deptsRes.status === 'fulfilled' && deptsRes.value.success) {
          allResults.push(...(deptsRes.value.data || []).map((d) => ({
            id: d.id, first_name: d.name, last_name: '', _type: 'department' as const, _label: 'Reparto',
          })));
        }
        const filtered = allResults.filter(
          (u) => !selectedRecipients.some(s => s.id === u.id) &&
            !(hasGroupOrDept && (u._type === 'group' || u._type === 'department'))
        );
        setRecipientResults(filtered);
        setShowRecipientDropdown(filtered.length > 0);
      } catch { /* silent */ }
    }, 300);
    return () => { if (recipientTimerRef.current) clearTimeout(recipientTimerRef.current); };
  }, [recipientSearch, selectedRecipients, hasGroupOrDept]);

  const addRecipient = (u: RecipientEntity) => {
    if (hasGroupOrDept && (u._type === 'group' || u._type === 'department')) return;
    if ((u._type === 'group' || u._type === 'department') && selectedRecipients.some(r => r._type === 'user')) {
      setSelectedRecipients([u]);
    } else {
      setSelectedRecipients(prev => [...prev, u]);
    }
    setRecipientSearch('');
    setRecipientResults([]);
    setShowRecipientDropdown(false);
    setTimeout(() => recipientInputRef.current?.focus(), 50);
  };

  const removeRecipient = (id: string) => {
    setSelectedRecipients(prev => prev.filter(u => u.id !== id));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMentionUser = (userId: string) => {
    setMentionedUserIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const effectiveTargetType = fixedTargetType || targetType;
      const data: Record<string, unknown> = {
        post_type: postType,
        title: title || undefined,
        content_html: contentHtml || undefined,
        content_json: contentJson || undefined,
        target_type: effectiveTargetType,
      };
      if (fixedTargetId) data.target_id = fixedTargetId;

      if (mentionedUserIds.length > 0) {
        data.mentioned_user_ids = mentionedUserIds;
      }

      if (selectedTopicIds.length > 0) {
        data.topic_ids = selectedTopicIds;
      }

      if (targetType === 'direct' && selectedRecipients.length > 0) {
        const recipients: { recipient_type: string; recipient_id: string }[] = [];
        selectedRecipients.forEach(r => {
          if (r._type === 'group') {
            recipients.push({ recipient_type: 'workgroup', recipient_id: r.id });
          } else if (r._type === 'department') {
            recipients.push({ recipient_type: 'department', recipient_id: r.id });
          } else {
            recipients.push({ recipient_type: 'user', recipient_id: r.id });
          }
        });
        data.recipients = recipients;
      }

      if (postType === 'poll') {
        data.poll = {
          question: pollQuestion,
          options: pollOptions.filter(o => o.trim()),
          is_multiple_choice: pollMultiple,
          is_anonymous: pollAnonymous,
        };
      }

      if (postType === 'event') {
        data.event = {
          title: eventTitle,
          start_at: eventStartDate && eventStartTime ? `${eventStartDate}T${eventStartTime}` : eventStartDate || undefined,
          end_at: eventEndDate && eventEndTime ? `${eventEndDate}T${eventEndTime}` : eventEndDate || undefined,
          location: eventLocation || undefined,
          description: eventDescription || undefined,
        };
        data.title = data.title || eventTitle;
      }

      let publishOk = false;
      let publishedPostId: string | null = null;

      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        formData.append('post_type', postType);
        if (title) formData.append('title', title);
        if (contentHtml) formData.append('content_html', contentHtml);
        formData.append('target_type', effectiveTargetType);
        if (fixedTargetId) formData.append('target_id', fixedTargetId);
        if (targetType === 'direct' && selectedRecipients.length > 0) {
          const recipients = selectedRecipients.map(r => ({
            recipient_type: r._type === 'group' ? 'workgroup' : r._type === 'department' ? 'department' : 'user',
            recipient_id: r.id,
          }));
          formData.append('recipients', JSON.stringify(recipients));
        }
        if (data.poll) formData.append('poll', JSON.stringify(data.poll));
        if (data.event) formData.append('event', JSON.stringify(data.event));
        if (data.mentioned_user_ids) formData.append('mentioned_user_ids', JSON.stringify(data.mentioned_user_ids));
        try {
          const result = await apiClient.post<FeedPost>('/messenger/feed/posts/upload', formData);
          if (result.success && result.data) {
            await onSubmit({ ...data, _uploaded: true, _uploadedPost: result.data });
            publishOk = true;
            publishedPostId = result.data.id;
          } else {
            await onSubmit(data);
            publishOk = true;
          }
        } catch {
          await onSubmit(data);
          publishOk = true;
        }
      } else {
        await onSubmit(data);
        publishOk = true;
      }

      // Task #1679 — log audit feedback suggerimenti topic SOLO dopo publish OK.
      const suggestedIds = lastSuggestedIdsRef.current;
      if (publishOk && suggestedIds.length > 0) {
        const accepted = suggestedIds.filter(id => selectedTopicIds.includes(id));
        void apiClient.post('/messenger/feed/topics/suggest/feedback', {
          suggested_topic_ids: suggestedIds,
          accepted_topic_ids: accepted,
          ...(publishedPostId ? { post_id: publishedPostId } : {}),
        }).catch(() => { /* silent */ });
        lastSuggestedIdsRef.current = [];
      }

      setTitle('');
      setContentHtml('');
      setContentJson(null);
      setPostType('message');
      setPollQuestion('');
      setPollOptions(['', '']);
      setEventTitle('');
      setEventStartDate('');
      setEventStartTime('');
      setEventEndDate('');
      setEventEndTime('');
      setEventLocation('');
      setEventDescription('');
      setFiles([]);
      setSelectedRecipients([]);
      setMentionedUserIds([]);
      setSelectedTopicIds([]);
      setIsExpanded(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("bg-white dark:bg-gray-900 rounded-xl border shadow-sm overflow-hidden", className)}>
      {!isExpanded ? (
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${getAvatarGradient(userName)} flex items-center justify-center text-white font-semibold text-sm shrink-0`}>
              {userInitial}
            </div>
            <button
              onClick={() => setIsExpanded(true)}
              className="flex-1 text-left px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
            >
              Cosa vuoi condividere con il team?
            </button>
          </div>
          <div className="flex items-center gap-1 mt-3 ml-13 pl-[52px]">
            {POST_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => { setPostType(type.value); setIsExpanded(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <type.icon className="h-3.5 w-3.5" />
                {type.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center border-b bg-gray-50 dark:bg-gray-800/50">
            {POST_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => setPostType(type.value)}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px",
                  postType === type.value
                    ? "border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300"
                )}
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </button>
            ))}
          </div>

          <div className="p-4 space-y-3">
            {!fixedTargetType && <div className="relative">
              <div className="flex flex-wrap items-center gap-1.5 p-2 min-h-[40px] border rounded-lg bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 transition-all">
                {selectedRecipients.length === 0 && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-md shrink-0">
                    <Globe className="h-3 w-3" /> Tutti
                  </span>
                )}
                {selectedRecipients.map(r => {
                  const t = r._type || 'user';
                  const IconComp = TYPE_ICON[t] || UserCircle;
                  return (
                    <Badge key={r.id} variant="secondary" className={cn("gap-1 text-xs pl-1.5 pr-1 py-0.5", TYPE_COLORS[t])}>
                      <IconComp className="h-3 w-3" />
                      {r.first_name} {r.last_name}
                      <button onClick={() => removeRecipient(r.id)} className="hover:text-red-500 transition-colors ml-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
                <input
                  ref={recipientInputRef}
                  type="text"
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                  placeholder={selectedRecipients.length === 0 ? 'Cerca utenti, gruppi o reparti da aggiungere...' : 'Aggiungi...'}
                  className="flex-1 min-w-[140px] bg-transparent text-sm outline-none placeholder:text-gray-400"
                  onFocus={() => { if (recipientResults.length > 0) setShowRecipientDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowRecipientDropdown(false), 200)}
                />
              </div>
              {showRecipientDropdown && recipientResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-56 overflow-auto">
                  {recipientResults.map((u) => {
                    const t = u._type || 'user';
                    const IconComp = TYPE_ICON[t] || UserCircle;
                    return (
                      <button
                        key={`${t}-${u.id}`}
                        onMouseDown={() => addRecipient(u)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                      >
                        <div className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0", t === 'user' ? `bg-gradient-to-br ${getAvatarGradient(`${u.first_name || ''} ${u.last_name || ''}`)} text-white` : TYPE_COLORS[t])}>
                          {t === 'user' ? <span className="text-[11px] font-semibold">{(u.first_name || '?')[0]}</span> : <IconComp className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs truncate">{u.first_name} {u.last_name}</p>
                          <p className="text-[10px] text-gray-400">{u._label}{u.email ? ` · ${u.email}` : ''}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>}

            {suggestedTopics.filter(s => !selectedTopicIds.includes(s.id)).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 mr-1">
                  <Sparkles className="h-3 w-3" /> Suggeriti{isSuggesting ? '…' : ':'}
                </span>
                {suggestedTopics.filter(s => !selectedTopicIds.includes(s.id)).map(s => {
                  const disabled = selectedTopicIds.length >= 3;
                  return (
                    <span key={s.id} className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => acceptSuggestion(s.id)}
                        title={`Aggiungi topic suggerito (${s.reason})`}
                        className={cn(
                          "inline-flex items-center gap-1 pl-2 pr-1.5 py-0.5 rounded-l-full text-[11px] font-medium transition-all",
                          disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                        )}
                        style={s.color ? { color: s.color } : undefined}
                      >
                        <Plus className="h-3 w-3" />
                        {s.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(s.id)}
                        title="Ignora suggerimento"
                        className="px-1 py-0.5 rounded-r-full text-indigo-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {availableTopics.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 mr-1">
                  <Tag className="h-3 w-3" /> Topic ({selectedTopicIds.length}/3):
                </span>
                {availableTopics.map(t => {
                  const isSel = selectedTopicIds.includes(t.id);
                  const disabled = !isSel && selectedTopicIds.length >= 3;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => !disabled && toggleTopic(t.id)}
                      disabled={disabled}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all",
                        isSel
                          ? "border-transparent text-white shadow-sm"
                          : disabled
                            ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600 dark:border-gray-700"
                            : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                      )}
                      style={isSel ? { backgroundColor: t.color || '#6366f1' } : undefined}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}

            {(postType === 'message') && (
              <Input
                placeholder="Titolo (opzionale)"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="font-semibold text-base border-0 border-b rounded-none px-0 focus-visible:border-blue-500"
              />
            )}

            {postType !== 'event' && (
              <FeedEditor
                onChange={(html, json) => { setContentHtml(html); setContentJson(json); }}
                placeholder={postType === 'file' ? 'Descrizione dei file allegati...' : 'Condividi un aggiornamento, un\'idea o una notizia... Usa @ per menzionare'}
                minHeight="100px"
                onMentionUser={handleMentionUser}
              />
            )}

            {postType === 'event' && (
              <div className="space-y-3 p-4 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20 rounded-xl border border-cyan-100 dark:border-cyan-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-cyan-500" />
                  <span className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">Nuovo Evento</span>
                </div>
                <Input
                  placeholder="Titolo evento"
                  value={eventTitle}
                  onChange={e => setEventTitle(e.target.value)}
                  className="font-semibold bg-white dark:bg-gray-900"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Data inizio</Label>
                    <Input
                      type="date"
                      value={eventStartDate}
                      onChange={e => setEventStartDate(e.target.value)}
                      className="bg-white dark:bg-gray-900 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Ora inizio</Label>
                    <Input
                      type="time"
                      value={eventStartTime}
                      onChange={e => setEventStartTime(e.target.value)}
                      className="bg-white dark:bg-gray-900 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Data fine</Label>
                    <Input
                      type="date"
                      value={eventEndDate}
                      onChange={e => setEventEndDate(e.target.value)}
                      className="bg-white dark:bg-gray-900 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Ora fine</Label>
                    <Input
                      type="time"
                      value={eventEndTime}
                      onChange={e => setEventEndTime(e.target.value)}
                      className="bg-white dark:bg-gray-900 text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                  <Input
                    placeholder="Luogo (opzionale)"
                    value={eventLocation}
                    onChange={e => setEventLocation(e.target.value)}
                    className="bg-white dark:bg-gray-900"
                  />
                </div>
                <textarea
                  placeholder="Descrizione evento..."
                  value={eventDescription}
                  onChange={e => setEventDescription(e.target.value)}
                  className="w-full p-3 text-sm bg-white dark:bg-gray-900 border rounded-lg resize-none min-h-[80px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500/30 focus:border-cyan-500"
                />
              </div>
            )}

            {postType === 'poll' && (
              <div className="space-y-3 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 rounded-xl border border-purple-100 dark:border-purple-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">Configurazione Sondaggio</span>
                </div>
                <Input
                  placeholder="Qual e' la domanda del sondaggio?"
                  value={pollQuestion}
                  onChange={e => setPollQuestion(e.target.value)}
                  className="font-medium bg-white dark:bg-gray-900"
                />
                <div className="space-y-2">
                  {pollOptions.map((option, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex items-center justify-center h-6 w-6 rounded-full border-2 border-purple-300 text-purple-500 text-xs font-bold shrink-0">
                        {i + 1}
                      </div>
                      <Input
                        placeholder={`Opzione ${i + 1}`}
                        value={option}
                        onChange={e => {
                          const next = [...pollOptions];
                          next[i] = e.target.value;
                          setPollOptions(next);
                        }}
                        className="bg-white dark:bg-gray-900"
                      />
                      {pollOptions.length > 2 && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-gray-400 hover:text-red-500" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {pollOptions.length < 10 && (
                    <Button variant="outline" size="sm" className="border-dashed border-purple-300 text-purple-600 hover:bg-purple-50" onClick={() => setPollOptions([...pollOptions, ''])}>
                      <Plus className="h-4 w-4 mr-1" /> Aggiungi opzione
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={pollMultiple} onCheckedChange={setPollMultiple} />
                    <Label className="text-sm">Scelta multipla</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={pollAnonymous} onCheckedChange={setPollAnonymous} />
                    <Label className="text-sm">Anonimo</Label>
                  </div>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border rounded-lg px-3 py-2">
                    <Paperclip className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="flex-1 text-sm truncate">{f.name}</span>
                    <span className="text-xs text-gray-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t mt-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  title="Allega file"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  Allega
                </button>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  {targetType === 'global' ? (
                    <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Visibile a tutti</span>
                  ) : (
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {selectedRecipients.length} destinatar{selectedRecipients.length === 1 ? 'io' : 'i'}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsExpanded(false);
                    setSelectedRecipients([]);
                    setMentionedUserIds([]);
                    setFiles([]);
                  }}
                  className="text-gray-500"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (!contentHtml && postType !== 'poll' && postType !== 'event' && files.length === 0)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                >
                  <Send className="h-3.5 w-3.5" />
                  {isSubmitting ? 'Invio...' : 'Pubblica'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
